import moment from "moment-timezone";

// Every date in the dashboard (and the ops app) is shown in one time zone, not
// whatever the browser/OS happens to be set to — so a warehouse tablet, an
// accountant's laptop and the access log all agree on "when". Default is
// Lisbon; it can be changed for the whole deployment (TIME_ZONE) and per
// person (Preferences → Time zone, kept in this browser).
export const DEFAULT_TIME_ZONE = "Europe/Lisbon";
export const TIME_ZONE_STORAGE_KEY = "dashboardTimeZone";

export const isValidTimeZone = (value: string | null | undefined): value is string =>
  !!value && !!moment.tz.zone(value);

const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Person's choice > deployment TIME_ZONE > Lisbon. */
export const getTimeZone = (
  configured: string | undefined = window.__SALEOR_CONFIG__?.TIME_ZONE,
  stored: string | null = readStored(),
): string => {
  if (isValidTimeZone(stored)) {
    return stored;
  }

  if (isValidTimeZone(configured)) {
    return configured;
  }

  return DEFAULT_TIME_ZONE;
};

export const saveTimeZone = (timeZone: string | null) => {
  try {
    if (timeZone && isValidTimeZone(timeZone)) {
      window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, timeZone);
    } else {
      window.localStorage.removeItem(TIME_ZONE_STORAGE_KEY);
    }
  } catch {
    // storage blocked: the deployment default still applies
  }
};

const withTimeZone = (options: Intl.DateTimeFormatOptions | undefined, timeZone: string) =>
  options?.timeZone ? options : { ...options, timeZone };

/**
 * Makes `timeZone` the default for everything that formats dates: moment,
 * Intl.DateTimeFormat (react-intl) and Date#toLocale*String. Explicit
 * `timeZone` options still win. Returns a function that undoes it (tests).
 */
export const applyTimeZone = (timeZone: string): (() => void) => {
  const NativeDateTimeFormat = Intl.DateTimeFormat;
  const { toLocaleString, toLocaleDateString, toLocaleTimeString } = Date.prototype;
  const previousMomentZone = (moment as unknown as { defaultZone?: { name: string } | null })
    .defaultZone?.name;

  moment.tz.setDefault(timeZone);

  Intl.DateTimeFormat = new Proxy(NativeDateTimeFormat, {
    construct: (target, [locales, options]) => new target(locales, withTimeZone(options, timeZone)),
    apply: (target, _this, [locales, options]) => target(locales, withTimeZone(options, timeZone)),
  });
  Date.prototype.toLocaleString = function (
    this: Date,
    locales?: Intl.LocalesArgument,
    options?: Intl.DateTimeFormatOptions,
  ) {
    return toLocaleString.call(this, locales, withTimeZone(options, timeZone));
  };
  Date.prototype.toLocaleDateString = function (
    this: Date,
    locales?: Intl.LocalesArgument,
    options?: Intl.DateTimeFormatOptions,
  ) {
    return toLocaleDateString.call(this, locales, withTimeZone(options, timeZone));
  };
  Date.prototype.toLocaleTimeString = function (
    this: Date,
    locales?: Intl.LocalesArgument,
    options?: Intl.DateTimeFormatOptions,
  ) {
    return toLocaleTimeString.call(this, locales, withTimeZone(options, timeZone));
  };

  return () => {
    if (previousMomentZone) {
      moment.tz.setDefault(previousMomentZone);
    } else {
      moment.tz.setDefault();
    }

    Intl.DateTimeFormat = NativeDateTimeFormat;
    Date.prototype.toLocaleString = toLocaleString;
    Date.prototype.toLocaleDateString = toLocaleDateString;
    Date.prototype.toLocaleTimeString = toLocaleTimeString;
  };
};
